import { EmailTemplateDto } from "@babackapp/private-api-client/types";
import TemplatesEditor from "@babackapp/templates-editor/templates-editor";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@babackapp/ui/components/sheet";
import { useTranslation } from "react-i18next";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWorkflowForm } from "../../context/workflows-editor-workflows-context";
import { useNodeFormPath } from "../../hooks/use-node-form-path";
import type { EmailOverrides } from "./apply-email-overrides";
import { WorkflowSubjectEditor } from "./workflow-subject-editor";

interface EmailCustomizationSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  template: EmailTemplateDto;
}

type BlockEditorContent = {
  beforeBlocks: unknown[];
  insideBlocks: unknown[];
  afterBlocks: unknown[];
};

export function EmailCustomizationSheet({
  isOpen,
  onOpenChange,
  template,
}: EmailCustomizationSheetProps) {
  const { t } = useTranslation("trackoffice");
  const form = useWorkflowForm();
  const overridesPath = useNodeFormPath("config.overrides");

  // Local state for editing
  const [localSubject, setLocalSubject] = useState<Record<string, string>>({});
  const [localContent, setLocalContent] = useState<BlockEditorContent | null>(
    null,
  );
  const [hiddenBlocks, setHiddenBlocks] = useState<string[]>([]);
  const initializedRef = useRef(false);

  // Read existing overrides from form
  const existingOverrides = overridesPath
    ? (form.watch(overridesPath) as EmailOverrides | null)
    : null;

  // Initialize state when sheet opens
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      const originalSubject = (template.subject ?? {}) as Record<
        string,
        string
      >;
      const customSubject = existingOverrides?.subject ?? {};
      setLocalSubject({ ...originalSubject, ...customSubject });
      setHiddenBlocks(existingOverrides?.hiddenBlocks ?? []);
      setLocalContent(null);
      initializedRef.current = true;
    }
    if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen, template, existingOverrides]);

  // Initial overrides for the template editor (pre-apply customized blocks)
  const initialOverrides = useMemo(
    () => ({
      customBlocks: existingOverrides?.customBlocks,
      customSubject: existingOverrides?.subject,
    }),
    // Only compute once when sheet opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  );

  // Track which blocks have been customized
  const customizedBlockIds = useMemo(() => {
    if (!localContent) {
      return Object.keys(existingOverrides?.customBlocks ?? {});
    }
    return computeCustomizedBlockIds(template, localContent);
  }, [template, localContent, existingOverrides?.customBlocks]);

  const handleToggleBlockVisibility = useCallback((blockId: string) => {
    setHiddenBlocks((prev) =>
      prev.includes(blockId)
        ? prev.filter((id) => id !== blockId)
        : [...prev, blockId],
    );
  }, []);

  const handleFormChange = useCallback(
    (data: { content?: BlockEditorContent }) => {
      if (data.content) {
        // Deep clone to ensure React detects the state change
        // (react-hook-form watch may return same object references)
        setLocalContent(structuredClone(data.content));
      }
    },
    [],
  );

  const handleSave = () => {
    if (!overridesPath) return;

    const customBlocks = localContent
      ? computeCustomBlocks(template, localContent)
      : existingOverrides?.customBlocks ?? {};

    const customSubject = computeCustomSubject(
      (template.subject ?? {}) as Record<string, string>,
      localSubject,
    );

    const overrides = {
      ...(Object.keys(customSubject).length > 0 ? { subject: customSubject } : {}),
      ...(Object.keys(customBlocks).length > 0 ? { customBlocks } : {}),
      ...(hiddenBlocks.length > 0 ? { hiddenBlocks } : {}),
    };

    form.setValue(
      overridesPath,
      Object.keys(overrides).length > 0 ? overrides : undefined,
      { shouldDirty: true },
    );

    onOpenChange(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[85vw] p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-4 border-b shrink-0">
          <SheetTitle>
            {t(
              "workflowsEditor.emailCustomization.title",
              "Customize email content",
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="px-6 pb-3 border-b shrink-0">
          <WorkflowSubjectEditor
            value={localSubject}
            onChange={setLocalSubject}
          />
        </div>

        <div className="flex-1 overflow-hidden">
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full text-muted-foreground">
                {t("common.loading", "Loading...")}
              </div>
            }
          >
            <TemplatesEditor
              currentTemplateId={template.id}
              isWorkflowCustomMode={true}
              initialOverrides={initialOverrides}
              hiddenBlocks={hiddenBlocks}
              onToggleBlockVisibility={handleToggleBlockVisibility}
              customizedBlockIds={customizedBlockIds}
              onFormChange={handleFormChange}
              onWorkflowSave={handleSave}
              onWorkflowCancel={() => onOpenChange(false)}
              workflowSubject={localSubject}
            />
          </Suspense>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// --- Delta computation helpers ---

function computeCustomBlocks(
  template: EmailTemplateDto,
  currentContent: BlockEditorContent,
): Record<string, unknown> {
  const originalBlocks = getAllBlocks(template);
  const currentBlocks = [
    ...(currentContent.beforeBlocks ?? []),
    ...(currentContent.insideBlocks ?? []),
    ...(currentContent.afterBlocks ?? []),
  ] as Array<{ id: string }>;

  const customBlocks: Record<string, unknown> = {};

  for (const currentBlock of currentBlocks) {
    const original = originalBlocks.get(currentBlock.id);
    if (!original) continue;

    if (JSON.stringify(original) !== JSON.stringify(currentBlock)) {
      customBlocks[currentBlock.id] = currentBlock;
    }
  }

  return customBlocks;
}

function computeCustomizedBlockIds(
  template: EmailTemplateDto,
  currentContent: BlockEditorContent,
): string[] {
  const customBlocks = computeCustomBlocks(template, currentContent);
  return Object.keys(customBlocks);
}

function computeCustomSubject(
  originalSubject: Record<string, string>,
  currentSubject: Record<string, string>,
): Record<string, string> {
  const custom: Record<string, string> = {};

  for (const [locale, value] of Object.entries(currentSubject)) {
    if (originalSubject[locale] !== value) {
      custom[locale] = value;
    }
  }

  return custom;
}

function getAllBlocks(template: EmailTemplateDto): Map<string, unknown> {
  const blocks = new Map<string, unknown>();
  const content = template.content;
  if (!content) return blocks;

  for (const block of [
    ...(content.beforeBlocks ?? []),
    ...(content.insideBlocks ?? []),
    ...(content.afterBlocks ?? []),
  ]) {
    blocks.set(block.id, block);
  }

  return blocks;
}
